import type {
  SourceTemplates,
  AutocompleteSource,
  GetSources,
} from "@algolia/autocomplete-shared";
import {
  INDEX_NAME_BOOKS,
  INDEX_NAME_CONTRIBUTORS,
  INDEX_NAME_SUBSCRIPTIONS,
  type BookDocument,
  type ContributorDocument,
  type SubscriptionDocument,
} from "@cappelendamm/catalogue-sync/meilisearch";
import { getPageLink } from "@/navigation/selectors";
import { autocompleteClient } from "@/meilisearch/client";
import { getMeilisearchResults } from "@meilisearch/autocomplete-client";
import {
  AutocompleteTemplateHeader,
  AutocompleteTemplateItem,
  type AutocompleteTemplateItemProps,
} from "./SourceTemplates";

export type Source =
  | Partial<SubscriptionDocument>
  | Partial<BookDocument>
  | Partial<ContributorDocument>;

type HighlightedDocument<Document> = {
  _highlightResult: {
    [K in keyof Document]: Document[K] extends Array<infer U>
      ? Array<{
          value: U;
          fullyHighlighted: boolean;
          matchLevel: string;
          matchedWords: string[];
        }>
      : {
          value: Document[K];
          fullyHighlighted: boolean;
          matchLevel: string;
          matchedWords: string[];
        };
  };
} & Document;

export const getSourcesWithFilters =
  ({ filters }: { filters: string }): GetSources<Source> =>
  ({ query }) => {
    const contributorSource = {
      sourceId: INDEX_NAME_CONTRIBUTORS,
      getItemUrl({
        item,
      }: Parameters<
        NonNullable<AutocompleteSource<ContributorDocument>["getItemUrl"]>
      >[0]) {
        const { slug } = item;
        return getPageLink({ pageType: "contributor", params: slug });
      },
      getItems() {
        return getMeilisearchResults({
          searchClient: autocompleteClient,
          queries: [
            {
              indexName: INDEX_NAME_CONTRIBUTORS,
              query,
              params: {
                filters,
                attributesToHighlight: ["fullName"],
                attributesToRetrieve: ["fullName", "slug", "imageUrl"],
                hitsPerPage: 3,
              },
            },
          ],
        });
      },
      templates: {
        header() {
          return (
            <AutocompleteTemplateHeader>Forfatter</AutocompleteTemplateHeader>
          );
        },
        item({
          item,
          components,
        }: Parameters<SourceTemplates<ContributorDocument>["item"]>[0]) {
          const { fullName, slug, imageUrl } = item;
          const props: AutocompleteTemplateItemProps = {
            imageSrc: imageUrl,
            altText: `Bilde av ${fullName}`,
            highlightName: components.Highlight({
              hit: item,
              attribute: ["fullName"],
            }),
            href: getPageLink({ pageType: "contributor", params: slug }),
          };
          return <AutocompleteTemplateItem {...props} />;
        },
      },
    };
    const bookSource = {
      sourceId: INDEX_NAME_BOOKS,
      getItemUrl({
        item,
      }: Parameters<
        NonNullable<AutocompleteSource<BookDocument>["getItemUrl"]>
      >[0]) {
        const { slug } = item;
        return getPageLink({ pageType: "book", params: slug });
      },
      getItems() {
        return getMeilisearchResults({
          searchClient: autocompleteClient,
          queries: [
            {
              indexName: INDEX_NAME_BOOKS,
              query,
              params: {
                filters,
                hitsPerPage: 3,
                attributesToHighlight: ["title", "authors"],
                attributesToRetrieve: [
                  "title",
                  "slug",
                  "imageUrl",
                  "format",
                  "price",
                  "authors",
                ],
              },
            },
          ],
        });
      },
      templates: {
        header() {
          return <AutocompleteTemplateHeader>Bøker</AutocompleteTemplateHeader>;
        },
        item({
          item,
          components,
        }: Parameters<
          SourceTemplates<HighlightedDocument<BookDocument>>["item"]
        >[0]) {
          const { title, slug, imageUrl, format, price, _highlightResult } =
            item;
          const props: AutocompleteTemplateItemProps = {
            imageSrc: imageUrl,
            altText: `Omslag av ${title}`,
            format: format,
            highlightName: components.Highlight({
              hit: item,
              attribute: "title",
            }),
            highlightDetails: components.Highlight({
              hit: {
                authors: _highlightResult.authors
                  .map(({ value }) => value)
                  .join(", "),
              },
              attribute: "authors",
            }),
            price: price ? `${price}` : undefined,
            href: getPageLink({ pageType: "book", params: slug }),
          };
          return <AutocompleteTemplateItem {...props} />;
        },
      },
    };
    const subscriptionSource = {
      sourceId: INDEX_NAME_SUBSCRIPTIONS,
      getItemUrl({
        item,
      }: Parameters<
        NonNullable<AutocompleteSource<SubscriptionDocument>["getItemUrl"]>
      >[0]) {
        const { slug } = item;
        return getPageLink({ pageType: "subscription", params: slug });
      },
      getItems() {
        return getMeilisearchResults({
          searchClient: autocompleteClient,
          queries: [
            {
              indexName: INDEX_NAME_SUBSCRIPTIONS,
              query,
              params: {
                filters,
                hitsPerPage: 3,
                attributesToHighlight: ["subscriptionName"],
                attributesToRetrieve: ["subscriptionName", "slug", "imageUrl"],
              },
            },
          ],
        });
      },
      templates: {
        header() {
          return (
            <AutocompleteTemplateHeader>Abonnement</AutocompleteTemplateHeader>
          );
        },
        item({
          item,
          components,
        }: Parameters<SourceTemplates<SubscriptionDocument>["item"]>[0]) {
          const { subscriptionName, slug, imageUrl } = item;
          const props: AutocompleteTemplateItemProps = {
            imageSrc: imageUrl,
            altText: `Omslag av ${subscriptionName}`,
            highlightName: components.Highlight({
              hit: item,
              attribute: "subscriptionName",
            }),
            href: getPageLink({ pageType: "subscription", params: slug }),
          };
          return <AutocompleteTemplateItem {...props} />;
        },
      },
    };
    return [
      subscriptionSource,
      bookSource,
      contributorSource,
    ] as unknown as Array<AutocompleteSource<Source>>;
  };
